import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  calcSellerProjectBreakdown,
  type SellerProjectRow,
} from "@/lib/sales-kpi-calc";

/**
 * KPI #6 — Selger-drilldown over fullførte prosjekter for en gitt selger.
 * Erik-lock 2026-05-15.
 *
 * Returnerer kun `project_status='completed'`-prosjekter eid av selgeren via
 * project_owners. Sortert descending på |budgetDeviationPct|.
 * Hooken returnerer tom liste hvis brukeren ikke er medlem av noen sales_unit
 * eller ikke har eierskap til fullførte prosjekter.
 */
export function useSellerProjectBreakdown(userId: string | null) {
  return useQuery({
    queryKey: ["seller-project-breakdown", userId],
    enabled: !!userId,
    queryFn: async (): Promise<SellerProjectRow[]> => {
      // 1. Sjekk om brukeren er medlem av en sales_unit
      const { data: memberships, error: memErr } = await supabase
        .from("sales_unit_members")
        .select("sales_unit_id")
        .eq("user_id", userId!);
      if (memErr) throw memErr;
      if (!memberships?.length) return [];

      // 2. Hent project_id-ene for prosjekter eid av denne brukeren
      const { data: ownerships, error: ownErr } = await supabase
        .from("project_owners")
        .select("project_id")
        .eq("user_id", userId!);
      if (ownErr) throw ownErr;
      const projectIds = (ownerships ?? []).map((o) => o.project_id);
      if (projectIds.length === 0) return [];

      // 3. Hent fullførte prosjekter med kunde + bf_diff_pct
      const { data: projects, error: projErr } = await supabase
        .from("projects")
        .select("id, name, project_code, total_amount, bf_diff_pct, customers(name)")
        .in("id", projectIds)
        .eq("project_status", "completed")
        .is("deleted_at", null);
      if (projErr) throw projErr;
      if (!projects?.length) return [];

      // 4. Hent total logged hours per prosjekt (lifetime, alle brukere)
      // Paginate: lifetime time_entries across a seller's projects grow past
      // the 1000-row cap (largest seller is at ~780 already). See VIB-1249.
      const timeEntries = await fetchAllPaginated<{
        project_id: string | null;
        duration_seconds: number | null;
      }>("time_entries", "project_id, duration_seconds", (q) =>
        q.in("project_id", projects.map((p) => p.id)).is("deleted_at", null),
      );

      const hoursMap = new Map<string, number>();
      for (const te of timeEntries) {
        if (!te.project_id) continue;
        hoursMap.set(
          te.project_id,
          (hoursMap.get(te.project_id) ?? 0) + (te.duration_seconds ?? 0) / 3600,
        );
      }

      return calcSellerProjectBreakdown(
        projects.map((p) => ({
          id: p.id,
          name: p.name,
          project_code: p.project_code,
          customer_name: (p.customers as { name?: string } | null)?.name ?? null,
          total_amount: p.total_amount,
          bf_diff_pct: p.bf_diff_pct,
          total_hours: hoursMap.get(p.id) ?? 0,
        })),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
